import React, { useState, useEffect, useCallback } from 'react';
import { getDashboardSummary } from '../services/api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(amount);
};

const COLORS = ['#1a365d', '#38a169', '#ed8936', '#e53e3e'];

const Overview = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('2025-12');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getDashboardSummary(selectedMonth);
      setData(response.data);
    } catch (err) {
      setError('Failed to load dashboard data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [selectedMonth]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  const { summary, companies } = data;

  const companyChartData = companies.map(c => ({
    name: c.code.toUpperCase(),
    income: c.totalIncome,
    expenses: c.totalExpenses,
    activeIncome: c.activeIncome,
  }));

  const pieData = companies.map(c => ({
    name: c.code.toUpperCase(),
    value: c.activeIncome,
  }));

  return (
    <div>
      <div className="filters">
        <div className="filter-group">
          <label>Month:</label>
          <select value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)}>
            <option value="2025-12">December 2025</option>
            <option value="2025-11">November 2025</option>
            <option value="2025-10">October 2025</option>
          </select>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="stats-grid">
        <div className="stat-card income">
          <span className="stat-label">Total Income</span>
          <span className="stat-value positive">{formatCurrency(summary.totalIncome)}</span>
        </div>
        <div className="stat-card expense">
          <span className="stat-label">Total Expenses</span>
          <span className="stat-value negative">{formatCurrency(summary.totalExpenses)}</span>
        </div>
        <div className="stat-card profit">
          <span className="stat-label">Net Profit</span>
          <span className={`stat-value ${summary.netProfit >= 0 ? 'positive' : 'negative'}`}>
            {formatCurrency(summary.netProfit)}
          </span>
        </div>
        <div className="stat-card salary">
          <span className="stat-label">Active Income</span>
          <span className="stat-value">{formatCurrency(summary.activeIncome)}</span>
        </div>
      </div>

      {/* Income Breakdown */}
      <div className="stats-grid">
        <div className="stat-card">
          <span className="stat-label">Passive Income (FD Interest)</span>
          <span className="stat-value">{formatCurrency(summary.passiveIncome)}</span>
        </div>
        <div className="stat-card">
          <span className="stat-label">Internal Transfers In</span>
          <span className="stat-value">{formatCurrency(summary.internalTransfersIn)}</span>
        </div>
        <div className="stat-card">
          <span className="stat-label">Internal Transfers Out</span>
          <span className="stat-value">{formatCurrency(summary.internalTransfersOut)}</span>
        </div>
        <div className="stat-card">
          <span className="stat-label">Total Transactions</span>
          <span className="stat-value">{summary.totalTransactions}</span>
        </div>
      </div>

      {/* Charts */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '24px', marginBottom: '24px' }}>
        <div className="card">
          <h3 className="card-title">Income by Company</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={companyChartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis tickFormatter={(v) => `₹${(v/1000).toFixed(0)}K`} />
              <Tooltip formatter={(v) => formatCurrency(v)} />
              <Legend />
              <Bar dataKey="activeIncome" name="Active Income" fill="#38a169" />
              <Bar dataKey="expenses" name="Expenses" fill="#e53e3e" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="card">
          <h3 className="card-title">Active Income Distribution</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                outerRadius={100}
                dataKey="value"
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
              >
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(v) => formatCurrency(v)} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Company Breakdown Table */}
      <div className="card">
        <h3 className="card-title">Company-wise Breakdown</h3>
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Company</th>
                <th>Total Income</th>
                <th>Expenses</th>
                <th>Active Income</th>
                <th>Passive Income</th>
                <th>Net Profit</th>
              </tr>
            </thead>
            <tbody>
              {companies.map((company) => (
                <tr key={company.id}>
                  <td>
                    <strong>{company.name}</strong>
                    <span className="company-code">{company.code}</span>
                  </td>
                  <td className="amount-credit">{formatCurrency(company.totalIncome)}</td>
                  <td className="amount-debit">{formatCurrency(company.totalExpenses)}</td>
                  <td>{formatCurrency(company.activeIncome)}</td>
                  <td>{formatCurrency(company.passiveIncome)}</td>
                  <td className={company.netProfit >= 0 ? 'amount-credit' : 'amount-debit'}>
                    {formatCurrency(company.netProfit)}
                  </td>
                </tr>
              ))}
              <tr style={{ fontWeight: 'bold', background: '#f7fafc' }}>
                <td>TOTAL</td>
                <td className="amount-credit">{formatCurrency(summary.totalIncome)}</td>
                <td className="amount-debit">{formatCurrency(summary.totalExpenses)}</td>
                <td>{formatCurrency(summary.activeIncome)}</td>
                <td>{formatCurrency(summary.passiveIncome)}</td>
                <td className={summary.netProfit >= 0 ? 'amount-credit' : 'amount-debit'}>
                  {formatCurrency(summary.netProfit)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Overview;
